import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Text, VStack } from '@chakra-ui/react';
import { useFetch, useIsMobile, useResource } from 'app/utils/hooks';
import { ApiErrorCodeMessages, ApiErrorCodeTypes } from 'types/enums';
import { addAlertAction } from 'app/store/Alerts/slice';
import { apiErrorHandler } from 'app/utils/utils';
import { useAppDispatch } from 'app/store';
import { resetPasswordValidationSchema } from 'app/components/PasswordReset/utils';
import { getPasswordPolicy } from 'app/store/Auth/selectors';
import {
  ChakraAuthContentWrapper,
  PasswordInput,
  TokenInput,
} from 'app/components/Core';
import { LogInLink } from 'app/components/PasswordReset/LogInLink';
import { PasswordPolicyMessage } from 'app/components/Core/PasswordPolicyMessage';
import { ResendCode } from 'app/components/ResendCode';
import { fetchPasswordPolicyEffect } from 'app/store/Auth/effects';

interface Request {
  email: string;
  newPassword: string;
  code: string;
}

interface Form {
  token: string;
  password: string;
  passwordRepeat: string;
}

const defaultValues: Form = {
  token: '',
  password: '',
  passwordRepeat: '',
};

interface PasswordResetFormProps {
  onConfirm: () => void;
  confirmLink: string;
  email: string;
  resetLink: string;
}

export const CodeConfirmForm: React.FC<PasswordResetFormProps> = ({
  onConfirm,
  confirmLink,
  email,
  resetLink,
}) => {
  const isMobileView = useIsMobile(false);
  const dispatch = useAppDispatch();
  const [passwordPolicy] = useResource(
    fetchPasswordPolicyEffect,
    getPasswordPolicy
  );
  const { callAPI } = useFetch<Request>();
  const navigate = useNavigate();

  const {
    handleSubmit,
    register,
    control,
    formState: { isValid, isSubmitting, errors },
    setError,
    clearErrors,
    watch,
    trigger,
  } = useForm<Form>({
    defaultValues,
    mode: 'onBlur',
    reValidateMode: 'onChange',
    resolver: yupResolver(resetPasswordValidationSchema(passwordPolicy)),
  });
  const [tokenError, setTokenError] = useState('');

  // Forces validating both password fields when both fields are filled in.
  const [passValue, repeatPassValue] = watch(['password', 'passwordRepeat']);
  useEffect(() => {
    if (passValue && repeatPassValue) trigger();
  }, [passValue, repeatPassValue, trigger]);

  const onSubmit = handleSubmit(async (data) => {
    try {
      clearErrors();
      const response = await callAPI(confirmLink, {
        email,
        newPassword: data.passwordRepeat,
        code: data.token,
      });
      if (response?.success) {
        onConfirm();
      }
    } catch (e: any) {
      const { error, response } = e?.response?.data || {};
      if (error.errorCode === ApiErrorCodeTypes.PASSWORD_TOO_WEAK) {
        setError('password', {
          message: ApiErrorCodeMessages[error.errorCode as ApiErrorCodeTypes],
        });
      } else if (error.errorCode === ApiErrorCodeTypes.INVALID_CODE) {
        setTokenError(
          ApiErrorCodeMessages[error.errorCode as ApiErrorCodeTypes]
        );
      } else if (
        error.errorCode === ApiErrorCodeTypes.USER_MUST_COMPLETE_MIGRATION
      ) {
        dispatch(
          addAlertAction({
            message: ApiErrorCodeMessages.USER_MUST_COMPLETE_MIGRATION,
          })
        );
        const redirectUrl = response?.redirectUrl;
        if (redirectUrl) {
          const { pathname, search } = new URL(redirectUrl);
          navigate(`${pathname.replace('/studio', '')}${search}`);
        }
      } else {
        dispatch(addAlertAction({ message: apiErrorHandler(e.response) }));
      }
    }
  });

  return (
    <ChakraAuthContentWrapper pt="56px">
      <Text w="full" color="textSubdued" fontSize="xs" fontWeight={500}>
        Reset your password
      </Text>
      <Text
        w="full"
        mt={isMobileView ? 0 : 1}
        mb={isMobileView ? 6 : 2}
        fontSize="lg"
        fontWeight={500}
        lineHeight={1.4}
        data-testid="primary-message"
      >
        Please use the code
        <br />
        we’ve emailed to you
      </Text>

      <VStack
        as="form"
        onSubmit={onSubmit}
        autoComplete="off"
        my={12}
        spacing={0}
      >
        <VStack minH={isMobileView ? '282px' : 72} spacing={0} w="full">
          <Controller
            control={control}
            name="token"
            render={({ field: { onChange } }) => (
              <TokenInput
                onChange={(val) => {
                  if (tokenError) setTokenError('');
                  onChange(val);
                }}
                error={tokenError}
              />
            )}
          />
          <PasswordInput
            label="New Password"
            {...register('password')}
            isInvalid={Boolean(errors?.password)}
            errorMessage={errors?.password?.message}
            data-testid="new-password"
          />
          <PasswordInput
            label="Confirm Password"
            isInvalid={Boolean(errors?.passwordRepeat)}
            errorMessage={errors?.passwordRepeat?.message}
            {...register('passwordRepeat')}
            data-testid="Confirm Password"
            errorMessageProps={{ 'data-testid': 'password-error-text' }}
            helperTextProps={{ 'data-testid': 'password-policy' }}
          />
          <PasswordPolicyMessage mt={0} {...{ passwordPolicy }} />
        </VStack>

        <Button
          w="full"
          type="submit"
          isDisabled={!isValid || isSubmitting || Boolean(tokenError)}
        >
          Set Password
        </Button>

        <ResendCode email={email} link={resetLink} py={12} />

        <LogInLink />
      </VStack>
    </ChakraAuthContentWrapper>
  );
};
